/* Phase 14A -- First-login push permission modal + iOS A2HS banner.
 *
 * P0 fix (2026-07-24): singleton-guarded, route-aware, remembers dismissal
 * for 30 days. Only ONE modal ever renders regardless of user reference
 * churn / re-renders / route changes.
 *
 * Behaviour contract:
 *   - Never renders on /login, /forgot-password, /invite/*.
 *   - Only renders when user is authenticated AND role !== CUSTOMER.
 *   - "Not now", Escape key, and backdrop click all dismiss the modal
 *     and set `sharda_push_optin_dismissed_at` (ms epoch) in localStorage.
 *   - Dismissal is honoured for 30 days.
 *   - Evaluation runs at most once per browser session (module-level
 *     guard) so repeat mounts / user reference changes cannot re-open it.
 */
import React, { useEffect, useRef, useState, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import { Bell, Share, PlusSquare, Smartphone, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/store/auth";
import {
  pushSupported, subscribeToPush, currentPermission, hasActivePushSubscription,
} from "@/lib/pushClient";
import {
  markFirstSeen, shouldShowIosPrompt, dismissIosPrompt, isIosSafariNotInstalled,
} from "@/lib/iosPwa";

/* localStorage keys */
const LEGACY_KEY = "sharda_push_prompt_shown_v1";   // pre-P0 name (kept for back-compat)
const DISMISS_KEY = "sharda_push_optin_dismissed_at"; // ms epoch of dismissal
const DISMISS_TTL_MS = 30 * 24 * 60 * 60 * 1000;      // 30 days

/* Module-level singleton guard -- ensures the "should I show?" evaluation
 * runs AT MOST ONCE per full page load. Prevents multiple modals stacking
 * when useEffect re-fires (user reference churn, route re-mounts, etc.). */
let evalStarted = false;
let modalEverShown = false;

/* Routes where the modal must NEVER appear (pre-auth surfaces). */
const BLOCKED_PATHS = ["/login", "/forgot-password", "/invite"];

function isDismissedRecently() {
  const raw = localStorage.getItem(DISMISS_KEY);
  if (!raw) return false;
  const ts = parseInt(raw, 10);
  if (!ts || Number.isNaN(ts)) return false;
  return Date.now() - ts < DISMISS_TTL_MS;
}

function persistDismiss() {
  localStorage.setItem(DISMISS_KEY, String(Date.now()));
  localStorage.setItem(LEGACY_KEY, "skipped");   // legacy path readers keep working
}

export default function PushPermissionProvider({ children }) {
  const user = useAuth((s) => s.user);
  const location = useLocation();
  const [showPushModal, setShowPushModal] = useState(false);
  const [showIosPrompt, setShowIosPrompt] = useState(false);
  const [busy, setBusy] = useState(false);
  const delayTimerRef = useRef(null);

  useEffect(() => { markFirstSeen(); }, []);

  /* Route-aware push-permission modal.
   * Evaluated at most once per session thanks to `evalStarted`. */
  useEffect(() => {
    if (!user || user.role === "CUSTOMER") return;
    if (BLOCKED_PATHS.some((p) => location.pathname.startsWith(p))) return;
    if (evalStarted) return;
    evalStarted = true;

    // Diagnostic log for P0 stacking bug -- confirms singleton guard fires exactly once.
    console.log("[push-optin] eval started once for user", user.id);

    let cancelled = false;
    (async () => {
      try {
        if (!pushSupported()) return;
        if (isDismissedRecently()) return;
        if (localStorage.getItem(LEGACY_KEY)) return; // honour legacy dismissals
        const perm = await currentPermission();
        const already = await hasActivePushSubscription();
        if (perm === "granted" && already) {
          localStorage.setItem(LEGACY_KEY, "auto");
          return;
        }
        if (perm === "denied") {
          localStorage.setItem(LEGACY_KEY, "denied");
          return;
        }
        if (cancelled) return;
        delayTimerRef.current = setTimeout(() => {
          if (modalEverShown) return;   // extra belt-and-braces
          modalEverShown = true;
          setShowPushModal(true);
        }, 1500);
      } catch (err) {
        console.warn("[push-optin] eval error", err);
      }
    })();

    return () => {
      cancelled = true;
      if (delayTimerRef.current) {
        clearTimeout(delayTimerRef.current);
        delayTimerRef.current = null;
      }
    };
  }, [user, location.pathname]);

  /* iOS A2HS prompt -- unchanged, but also gated on blocked paths. */
  useEffect(() => {
    if (!user || user.role === "CUSTOMER") return;
    if (BLOCKED_PATHS.some((p) => location.pathname.startsWith(p))) return;
    if (shouldShowIosPrompt()) setShowIosPrompt(true);
  }, [user, location.pathname]);

  const handleSkip = useCallback(() => {
    persistDismiss();
    setShowPushModal(false);
  }, []);

  /* Escape key + backdrop click both dismiss. */
  useEffect(() => {
    if (!showPushModal) return;
    const onKey = (e) => { if (e.key === "Escape") handleSkip(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showPushModal, handleSkip]);

  const handleEnable = useCallback(async () => {
    if (isIosSafariNotInstalled()) {
      setShowPushModal(false);
      setShowIosPrompt(true);
      return;
    }
    setBusy(true);
    try {
      await subscribeToPush();
      toast.success("Notifications enabled -- you'll get real-time alerts on this device.");
      localStorage.setItem(LEGACY_KEY, "granted");
      persistDismiss();
      setShowPushModal(false);
    } catch (e) {
      toast.error(e?.message || "Could not enable notifications");
    } finally { setBusy(false); }
  }, []);

  const onBackdropClick = useCallback((e) => {
    // Only dismiss when the backdrop itself was clicked, not the modal card.
    if (e.target === e.currentTarget) handleSkip();
  }, [handleSkip]);

  return (
    <>
      {children}

      <AnimatePresence>
        {showPushModal && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onBackdropClick}
            className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-3 sm:p-6"
            data-testid="push-permission-modal"
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
              transition={{ type: "spring", damping: 22 }}
              className="w-full max-w-md rounded-2xl bg-[var(--surface-elevated)] border border-[var(--surface-border)] shadow-2xl overflow-hidden"
              role="dialog"
              aria-modal="true"
            >
              <div className="p-5 sm:p-6 space-y-4 relative">
                <button
                  onClick={handleSkip}
                  aria-label="Dismiss"
                  data-testid="push-modal-close"
                  className="absolute top-3 right-3 h-8 w-8 rounded-full flex items-center justify-center text-foreground/60 hover:text-foreground hover:bg-white/[0.06] transition"
                >
                  <X className="h-4 w-4" />
                </button>
                <div className="flex items-start gap-4 pr-8">
                  <div className="h-12 w-12 rounded-xl bg-gradient-primary flex items-center justify-center shrink-0">
                    <Bell className="h-6 w-6 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h2 className="font-heading text-base tracking-[0.14em] uppercase">Stay in the loop</h2>
                    <p className="text-sm text-foreground/70 mt-1 leading-relaxed">
                      Get real-time push notifications for chat mentions, task assignments and reception alerts.
                      You can fine-tune what you receive in <b>Settings &rarr; Notifications</b>.
                    </p>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row-reverse gap-2 pt-1">
                  <button
                    onClick={handleEnable}
                    disabled={busy}
                    data-testid="push-modal-enable"
                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-primary text-white text-sm font-medium hover:brightness-110 disabled:opacity-60 transition"
                  >
                    <Bell className="h-4 w-4" />
                    {busy ? "Enabling..." : "Enable notifications"}
                  </button>
                  <button
                    onClick={handleSkip}
                    data-testid="push-modal-skip"
                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 border border-[var(--surface-border)] text-sm hover:bg-white/10 transition"
                  >
                    Not now
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showIosPrompt && (
          <motion.div
            initial={{ y: 200 }} animate={{ y: 0 }} exit={{ y: 200 }}
            transition={{ type: "spring", damping: 24 }}
            className="fixed bottom-3 left-3 right-3 sm:left-auto sm:right-4 sm:bottom-4 sm:max-w-sm z-[70]"
            data-testid="ios-a2hs-banner"
          >
            <div className="rounded-2xl bg-[var(--surface-elevated)] border border-[var(--surface-border)] shadow-2xl p-4 backdrop-blur-xl">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-xl bg-gradient-primary flex items-center justify-center shrink-0">
                  <Smartphone className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="font-heading text-xs tracking-widest uppercase">Install to enable push</h3>
                    <button
                      onClick={() => { dismissIosPrompt(); setShowIosPrompt(false); }}
                      data-testid="ios-a2hs-dismiss"
                      className="text-foreground/50 hover:text-foreground transition"
                      aria-label="Dismiss"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  <p className="text-xs text-foreground/70 mt-1.5 leading-relaxed">
                    On iOS, notifications only work when Sharda Energy OS is added to your Home Screen.
                    Tap <Share className="inline h-3.5 w-3.5 mx-0.5 -mt-0.5" /> Share, then
                    <PlusSquare className="inline h-3.5 w-3.5 mx-0.5 -mt-0.5" /> <b>Add to Home Screen</b>.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
